'use client';

import { useState } from 'react';
import { Image } from '@/app/_components/common/Image';
import type { ProductCardImage } from '@/app_components/AffiliateProductCard/types';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const HUNDRED = 100;
const IMAGECAROUSELBTN =
  'absolute top-1/2 flex w-10 h-10 md:w-8 md:h-8 min-w-0 min-h-0 items-center justify-center rounded-full bg-icon-white p-0 icon-primary hover:icon-primary-hover transition-opacity opacity-80 hover:opacity-100';

export const ImageCarousel = ({ images }: { images: ProductCardImage[] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const mobile = useMediaQuery(Breakpoints.Mobile);

  if (images.length === 0) {
    return null;
  }

  const hasMultiple = images.length > 1;

  const goPrev = () => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    setCurrentIndex((i) => Math.min(images.length - 1, i + 1));
  };

  return (
    <section
      className='flex flex-col gap-2'
      data-cy={ContentPlacement.AffiliateProductCardImageCarousel}
    >
      <div className='relative aspect-3/2 w-full overflow-hidden'>
        <div
          className='flex h-full transition-transform duration-300 ease-in-out'
          style={{ transform: `translateX(-${currentIndex * HUNDRED}%)` }}
        >
          {images.map((image) => (
            <div
              className={cn('relative w-full flex-shrink-0', !hasMultiple && 'border stroke-secondary rounded-sm')}
              key={image.url}
            >
              <Image
                alt={image.alt || ''}
                className='object-contain object-center'
                fill
                src={image.url || ''}
              />
            </div>
          ))}
        </div>
        {hasMultiple && (
          <>
            {currentIndex > 0 && (
              <ActionableIcon
                aria-label='Previous image'
                className={cn('left-2 -translate-y-1/2', IMAGECAROUSELBTN)}
                onClick={goPrev}
              >
                <Icon
                  icon='ChevronLeft'
                  size={mobile ? 'md' : 'sm'}
                  strokeWidth={1.5}
                />
              </ActionableIcon>
            )}
            {currentIndex < images.length - 1 && (
              <ActionableIcon
                aria-label='Next image'
                className={cn('right-2 -translate-y-1/2', IMAGECAROUSELBTN)}
                onClick={goNext}
              >
                <Icon
                  icon='ChevronRight'
                  size={mobile ? 'md' : 'sm'}
                  strokeWidth={1.5}
                />
              </ActionableIcon>
            )}
          </>
        )}
      </div>
      {hasMultiple && (
        <div className='flex gap-2'>
          {images.map((image, index) => (
            <button
              aria-label={`View image ${index + 1}`}
              className={cn(
                'relative aspect-square w-9 flex-shrink-0 overflow-hidden rounded-sm border transition-colors border-transparent hover:border-secondary opacity-50',
                currentIndex === index && 'border-[var(--color-blue-300)] opacity-100',
              )}
              key={`Button for Image ${image.url}`}
              onClick={() => setCurrentIndex(index)}
              type='button'
            >
              <Image
                alt={image.alt || ''}
                className='object-cover object-center'
                fill
                fullWidth={false}
                src={image.url || ''}
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
};
